import { motion } from 'motion/react';
import { memo, useState } from 'react';
import { BITTE_IMG } from '../../../lib/images';
import { cn } from '../../../lib/utils';

interface ThinkingMessageProps {
  agentImage?: string;
  agentName?: string;
  isWidgetMode?: boolean;
  disableAnimation?: boolean;
}

const ThinkingMessageComponent = ({
  agentImage,
  agentName,
  isWidgetMode = false,
  disableAnimation = false,
}: ThinkingMessageProps) => {
  // Synchronous height check to prevent delayed re-renders
  const getInitialScreenSize = () => {
    if (typeof window !== 'undefined') {
      return window.innerHeight > 800;
    }
    return true; // Default for SSR
  };

  const [isLargeScreen] = useState(getInitialScreenSize);
  const [currentSrc, setCurrentSrc] = useState<string>(agentImage ?? BITTE_IMG);

  const handleImageError = () => {
    setCurrentSrc(BITTE_IMG);
  };

  const content = (
    <div
      data-testid="message-assistant-loading"
      className={cn('bitte-w-full bitte-mx-auto bitte-max-w-3xl bitte-px-4', {
        // Desktop: Full padding (min-h-96 = 384px)
        'bitte-min-h-96': !isWidgetMode,

        // Widget on larger screens: Reduced padding (min-h-64 = 256px)
        'bitte-min-h-64': isWidgetMode && isLargeScreen,

        // Widget on smaller screens: Minimal padding (min-h-48 = 192px)
        'bitte-min-h-48': isWidgetMode && !isLargeScreen,
      })}
      data-role="assistant"
    >
      <div className="bitte-flex bitte-gap-4 bitte-w-full bitte-items-center">
        <div className="bitte-size-8 bitte-flex bitte-items-center bitte-rounded-full bitte-justify-center bitte-ring-1 bitte-shrink-0 bitte-ring-border bitte-bg-background">
          <img
            src={currentSrc}
            alt={agentName || 'Assistant'}
            className="bitte-w-6 bitte-h-6 bitte-rounded-full"
            onError={handleImageError}
          />
        </div>

        <div className="bitte-flex bitte-flex-col bitte-gap-2 bitte-w-full">
          <div className="bitte-flex bitte-items-center bitte-gap-4 bitte-text-muted-foreground">
            <div className="bitte-flex bitte-items-center bitte-gap-0.5">
              {[0, 0.2, 0.4].map((delay, i) => (
                <motion.div
                  key={i}
                  className="bitte-w-1 bitte-h-1 bitte-bg-current bitte-rounded-full"
                  animate={{
                    opacity: [0.3, 1, 0.3],
                  }}
                  transition={{
                    duration: 1.2,
                    repeat: Number.POSITIVE_INFINITY,
                    delay,
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Return regular div if animations are disabled, motion.div otherwise
  if (disableAnimation) {
    return content;
  }

  return (
    <motion.div
      initial={{ y: 5, opacity: 0 }}
      animate={{ y: 0, opacity: 1, transition: { delay: 1 } }}
    >
      {content}
    </motion.div>
  );
};

export const ThinkingMessage = memo(ThinkingMessageComponent);
